import { useApolloClient } from '@apollo/client';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { graphql } from '__generated__';

import type { RootState } from 'store';

import { CIError } from 'common/error/error_util';
import { usePageStatus } from 'common/hooks/use-page-status';
import { CILogger } from 'common/log/log.provider';
import { PasswordUpdated } from 'common/viewcomp/forms/password_updated';
import { CreatePasswordView } from 'module/auth/login/createpass/components/CreatePasswordView';
import 'module/auth/login/createpass/create_password.scss';

export interface CreatePasswordScreenProps {
    onCreated: () => void;
    showSucceededView: boolean;
    className: string;
}

export const CreatePasswordScreen = ({
    onCreated,
    showSucceededView,
    className,
}: CreatePasswordScreenProps): ReactElement => {
    const { t } = useTranslation('auth');
    const pageStatus = usePageStatus();
    const client = useApolloClient();
    const completedUser = useSelector((state: RootState) => state.auth.activeUser);
    const authError = useSelector((state: RootState) => state.auth.error);
    const handledRef = useRef(false);

    useEffect(() => {
        if (completedUser && !handledRef.current) {
            handledRef.current = true;
            if (completedUser.userType !== 'Patient') {
                client
                    .mutate({ mutation: SEND_PASSWORD_CHANGED_EMAIL, variables: { userId: completedUser.pk } })
                    .catch((err) => {
                        CILogger.getInstance().error(
                            `Failed to send password-changed email for user ${completedUser.pk}: ${err?.message}`
                        );
                        pageStatus.error(t(CIError.fromGraphQLError(err)?.translationKey));
                    });
            }
            onCreated();
        }
    }, [completedUser, onCreated, client, pageStatus, t]);

    return (
        <div id="createPasswordView" className={className}>
            {showSucceededView && completedUser ? (
                <PasswordUpdated />
            ) : (
                <CreatePasswordView error={completedUser ? undefined : authError} />
            )}
        </div>
    );
};

const SEND_PASSWORD_CHANGED_EMAIL = graphql(`
    mutation SendPasswordChangedEmail($userId: String!) {
        sendUserPasswordChangedEmail(userId: $userId)
    }
`);
